var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { Group, Mesh, Vector3, BoxGeometry } from "three";
import { UVMapBox, disposeGeometryRecursively, buildPointLightGrid } from "@rendering/vortex-core";
import { buildBackCanvasMeshes, buildFrontCanvasMeshes } from "./canvasBuilder";
import { buildFrame } from "./frameBuilder";
export class CanvasPrintProduct {
  constructor(config) {
    var _a, _b;
    Object.defineProperty(this, "type", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: "canvas print"
    });
    Object.defineProperty(this, "config", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "initializedGeometry", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "surfaceMapping", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.config = config;
    const {
      fabricSubstrate
    } = this.config;
    const interiorFrameSubstrate = this.config.interiorOptions.frameOptions.substrate;
    // Designable portions of the canvas print
    this.surfaceMapping = {
      front: {
        page: 1,
        substrate: fabricSubstrate,
        fullBleed: this.config.fullBleed,
        focalPoint: {
          cameraView: new Vector3(0, 0, -1)
        }
      },
      left: {
        page: 1,
        substrate: fabricSubstrate,
        fullBleed: this.config.fullBleed,
        focalPoint: {
          cameraView: new Vector3(1, 0, -0.2).normalize()
        }
      },
      right: {
        page: 1,
        substrate: fabricSubstrate,
        fullBleed: this.config.fullBleed,
        focalPoint: {
          cameraView: new Vector3(-1, 0, -0.2).normalize()
        }
      },
      top: {
        page: 1,
        substrate: fabricSubstrate,
        fullBleed: this.config.fullBleed,
        focalPoint: {
          cameraView: new Vector3(0, -1, -0.2).normalize()
        }
      },
      bottom: {
        page: 1,
        substrate: fabricSubstrate,
        fullBleed: this.config.fullBleed,
        focalPoint: {
          cameraView: new Vector3(0, 1, -0.2).normalize()
        }
      }
    };
    // Add 5 fabric components for the back of the canvas
    this.surfaceMapping["back-center"] = {
      substrate: fabricSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["back-left"] = {
      substrate: fabricSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["back-right"] = {
      substrate: fabricSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["back-top"] = {
      substrate: fabricSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["back-bottom"] = {
      substrate: fabricSubstrate,
      fullBleed: this.config.fullBleed
    };
    // Add four components interior frame segments
    this.surfaceMapping["interior-frame-left"] = {
      substrate: interiorFrameSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["interior-frame-right"] = {
      substrate: interiorFrameSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["interior-frame-top"] = {
      substrate: interiorFrameSubstrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["interior-frame-bottom"] = {
      substrate: interiorFrameSubstrate,
      fullBleed: this.config.fullBleed
    };
    // Add optional cover
    if ((_a = this.config.exteriorOptions) === null || _a === void 0 ? void 0 : _a.cover) {
      const coverOptions = this.config.exteriorOptions.cover;
      this.addCover(coverOptions.substrate, coverOptions.depth);
    }
    // Add optional exterior frame
    if ((_b = this.config.exteriorOptions) === null || _b === void 0 ? void 0 : _b.frameOptions) {
      this.addFrame(this.config.exteriorOptions.frameOptions);
    }
  }
  getGeometry() {
    return __awaiter(this, void 0, void 0, function* () {
      var _a, _b;
      // If existing geometry already exists dispose of it
      if (this.initializedGeometry) {
        disposeGeometryRecursively(this.initializedGeometry);
      }
      const dimensions = {
        width: this.config.fullBleed.width,
        height: this.config.fullBleed.height,
        depth: this.config.depth
      };
      const outerUVCoords = this.computeOuterUVCoords();
      const innerUVCoords = this.computeInnerUVCoords(outerUVCoords);
      this.initializedGeometry = new Group();
      // Add all the core canvas print geometry
      this.initializedGeometry.add(...buildFrontCanvasMeshes(dimensions, innerUVCoords, outerUVCoords));
      this.initializedGeometry.add(...buildBackCanvasMeshes(dimensions, innerUVCoords, outerUVCoords));
      this.initializedGeometry.add(...this.buildInteriorFrame(dimensions));
      // Add optional cover
      if ((_a = this.config.exteriorOptions) === null || _a === void 0 ? void 0 : _a.cover) {
        this.initializedGeometry.add(...this.buildExteriorCover(dimensions, this.config.exteriorOptions.cover.depth));
      }
      // Add optional frame
      if ((_b = this.config.exteriorOptions) === null || _b === void 0 ? void 0 : _b.frameOptions) {
        this.initializedGeometry.add(...this.buildExteriorFrameInner(dimensions, this.config.exteriorOptions.frameOptions));
        this.initializedGeometry.add(...this.buildExteriorFrameOuter(dimensions, this.config.exteriorOptions.frameOptions));
      }
      return this.initializedGeometry;
    });
  }
  getSurfaceMapping() {
    return this.surfaceMapping;
  }
  setProductSurface(surfaceId, productSurface) {
    if (this.surfaceMapping[surfaceId]) {
      this.surfaceMapping[surfaceId] = productSurface;
    } else {
      throw new Error("'".concat(surfaceId, "' is not a surface on this canvas product"));
    }
  }
  getStates() {
    return [{
      id: "default"
    }];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setStates(_productState) {}
  getCurrentState() {
    return {
      id: "default"
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCurrentState(_stateId) {}
  getAnimationIds() {
    return [];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  buildAnimation(_animationOptions) {
    throw new Error("Method not implemented for Canvas products.");
  }
  getAreaLights(pointLightIntensity,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _directionalLightIntensity) {
    const pointLightIntensities = [0.42 * pointLightIntensity, 0.42 * pointLightIntensity, 0.42 * pointLightIntensity, 0.42 * pointLightIntensity, 0.3675 * pointLightIntensity, 0.3675 * pointLightIntensity];
    return buildPointLightGrid(pointLightIntensities);
  }
  preLoad() {
    return Promise.resolve();
  }
  dispose() {
    // Nothing to do
    if (!this.initializedGeometry) return;
    disposeGeometryRecursively(this.initializedGeometry);
    this.initializedGeometry = undefined;
  }
  addCover(substrate, depth) {
    if (this.config.exteriorOptions) {
      this.config.exteriorOptions.cover = {
        substrate,
        depth
      };
    } else {
      this.config.exteriorOptions = {
        cover: {
          substrate,
          depth
        }
      };
    }
    this.surfaceMapping.cover = {
      substrate,
      fullBleed: this.config.fullBleed
    };
  }
  addFrame(frameOptions) {
    if (this.config.exteriorOptions) {
      this.config.exteriorOptions.frameOptions = frameOptions;
    } else {
      this.config.exteriorOptions = {
        frameOptions
      };
    }
    // Add four inner segments for the external frame
    this.surfaceMapping["exterior-frame-inner-left"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["exterior-frame-inner-right"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["exterior-frame-inner-top"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["exterior-frame-inner-bottom"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    // Add four outer segments for the external frame
    this.surfaceMapping["exterior-frame-outer-left"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["exterior-frame-outer-right"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["exterior-frame-outer-top"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
    this.surfaceMapping["exterior-frame-outer-bottom"] = {
      substrate: frameOptions.substrate,
      fullBleed: this.config.fullBleed
    };
  }
  // Builds and UV maps the interior wooden frame meshes of the canvas print
  buildInteriorFrame(dimensions) {
    // The thickness of the interior frame
    const {
      thickness
    } = this.config.interiorOptions.frameOptions;
    const frameDimensions = {
      width: dimensions.width * 0.96 - thickness * 2,
      height: dimensions.height * 0.96 - thickness * 2,
      depth: dimensions.depth * 0.9
    };
    return buildFrame(frameDimensions, thickness, dimensions.depth * 0.9, 0, 0, "interior-frame");
  }
  // Builds and UV maps the optional external covering mesh on the back of the canvas print
  buildExteriorCover(canvasDimensions, coverDepth) {
    const meshes = [];
    const coverGeometry = new BoxGeometry(canvasDimensions.width * 0.99, canvasDimensions.height * 0.99, coverDepth);
    UVMapBox(coverGeometry);
    const coverMesh = new Mesh(coverGeometry);
    coverMesh.uuid = "cover";
    coverMesh.position.setZ(-canvasDimensions.depth * 0.5 - coverDepth * 0.5);
    meshes.push(coverMesh);
    return meshes;
  }
  // Builds and UV maps the optional external frame base meshes on the back of the canvas print
  buildExteriorFrameInner(dimensions, frameOptions) {
    const meshes = [];
    const verticalGeometry = new BoxGeometry(frameOptions.inner.thickness, dimensions.height + frameOptions.inner.thickness, frameOptions.inner.depth);
    UVMapBox(verticalGeometry);
    const leftVerticalMesh = new Mesh(verticalGeometry);
    leftVerticalMesh.uuid = "exterior-frame-inner-left";
    leftVerticalMesh.position.setX(-dimensions.width * 0.5);
    leftVerticalMesh.position.setZ(-dimensions.depth * 0.5 - frameOptions.inner.depth * 0.5);
    const rightVerticalMesh = new Mesh(verticalGeometry);
    rightVerticalMesh.uuid = "exterior-frame-inner-right";
    rightVerticalMesh.position.setX(dimensions.width * 0.5);
    rightVerticalMesh.position.setZ(-dimensions.depth * 0.5 - frameOptions.inner.depth * 0.5);
    meshes.push(leftVerticalMesh);
    meshes.push(rightVerticalMesh);
    const horizontalGeometry = new BoxGeometry(dimensions.width, frameOptions.inner.thickness, frameOptions.inner.depth);
    UVMapBox(horizontalGeometry);
    const topHorizontalMesh = new Mesh(horizontalGeometry);
    topHorizontalMesh.uuid = "exterior-frame-inner-top";
    topHorizontalMesh.position.setY(dimensions.height * 0.5);
    topHorizontalMesh.position.setZ(-dimensions.depth * 0.5 - frameOptions.inner.depth * 0.5);
    const bottomHorizontalMesh = new Mesh(horizontalGeometry);
    bottomHorizontalMesh.uuid = "exterior-frame-inner-bottom";
    bottomHorizontalMesh.position.setY(-dimensions.height * 0.5);
    bottomHorizontalMesh.position.setZ(-dimensions.depth * 0.5 - frameOptions.inner.depth * 0.5);
    meshes.push(topHorizontalMesh);
    meshes.push(bottomHorizontalMesh);
    return meshes;
  }
  // Builds and UV maps the optional external frame body meshes on the side of the canvas print
  buildExteriorFrameOuter(dimensions, frameOptions) {
    // The thickness and depth of the exterior frame
    const {
      thickness,
      depth
    } = frameOptions.outer;
    // The distance from the canvas that the frame should begin
    const framePadding = frameOptions.inner.thickness * 0.5;
    // The distance from the back of the canvas to position the frame
    const frameOffset = frameOptions.inner.depth;
    return buildFrame(dimensions, thickness, depth, framePadding, frameOffset, "exterior-frame-outer");
  }
  computeOuterUVCoords() {
    if (this.config.trim) {
      const {
        trim
      } = this.config;
      return [trim.topLeftX / this.config.fullBleed.width, trim.topLeftY / this.config.fullBleed.height, trim.bottomRightX / this.config.fullBleed.width, trim.bottomRightY / this.config.fullBleed.height];
    }
    // No trim return default UV coords
    return [0.0, 0.0, 1.0, 1.0];
  }
  computeInnerUVCoords(outerCoords) {
    const flattenedPrintDimensions = {
      width: this.config.fullBleed.width + this.config.depth * 2,
      height: this.config.fullBleed.height + this.config.depth * 2
    };
    const sideRatio = {
      x: this.config.depth / flattenedPrintDimensions.width,
      y: this.config.depth / flattenedPrintDimensions.height
    };
    return [outerCoords[0] + sideRatio.x, outerCoords[1] + sideRatio.y, outerCoords[2] - sideRatio.x, outerCoords[3] - sideRatio.y];
  }
}